export const pattern = {
  mobie:
    /^((\+?86)|(\(\+86\)))?(13[012356789][0-9]{8}|15[012356789][0-9]{8}|17[012356789][0-9]{8}|19[012356789][0-9]{8}|18[02356789][0-9]{8}|147[0-9]{8}|1349[0-9]{7})$/,
  english: /^[a-zA-Z]+$/,
  englishAndNumber: /^[a-zA-Z0-9]+$/,
  englishAndUnderscore: /^[a-zA-Z][a-zA-Z0-9_]*$/,
  // 密码强度验证模式
  hasLowerCase: /[a-z]/, // 包含小写字母
  hasUpperCase: /[A-Z]/, // 包含大写字母
  hasNumber: /\d/, // 包含数字
  hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, // 包含特殊字符
};

/**
 * 邮箱
 * @param str
 */
export function isEmail(str: string) {
  return /^([a-zA-Z0-9_-])+@([a-zA-Z0-9_-])+((.[a-zA-Z0-9_-]{2,3}){1,2})$/.test(str);
}

/**
 * 手机号码
 * @param str
 */
export function isMobile(str: string) {
  return pattern.mobie.test(str);
}

/**
 * 电话号码
 * @param str
 */
export function isPhone(str: string) {
  return /^([0-9]{3,4}-)?[0-9]{7,8}$/.test(str);
}

/**
 * 密码强度验证（防止弱密码）
 * @param password 密码字符串
 * @returns 返回验证结果对象
 */
export function validatePasswordStrength(password: string): {
  isValid: boolean;
  message: string;
  score: number; // 密码强度评分（1-5）
} {
  if (!password) {
    return {
      isValid: false,
      message: '密码不能为空',
      score: 0,
    };
  }

  // 最小长度检查
  if (password.length < 8) {
    return {
      isValid: false,
      message: '密码长度不能少于8位',
      score: 1,
    };
  }

  // 最大长度检查
  if (password.length > 32) {
    return {
      isValid: false,
      message: '密码长度不能超过32位',
      score: 1,
    };
  }

  let score = 0;
  let strengthRequirements: string[] = [];

  // 检查各种字符类型
  const hasLower = pattern.hasLowerCase.test(password);
  const hasUpper = pattern.hasUpperCase.test(password);
  const hasNumber = pattern.hasNumber.test(password);
  const hasSpecial = pattern.hasSpecialChar.test(password);

  if (hasLower) score++;
  else strengthRequirements.push('小写字母');

  if (hasUpper) score++;
  else strengthRequirements.push('大写字母');

  if (hasNumber) score++;
  else strengthRequirements.push('数字');

  if (hasSpecial) score++;
  else strengthRequirements.push('特殊字符');

  // 长度加分
  if (password.length >= 12) score++;

  // 至少需要包含3种不同类型的字符
  const typeCount = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;
  if (typeCount < 3) {
    return {
      isValid: false,
      message: `密码强度不足，请包含以下字符类型：${strengthRequirements.join('、')}`,
      score: Math.min(score, 2),
    };
  }

  // 检查常见弱密码模式
  const weakPatterns = [
    /^\d+$/, // 纯数字
    /^[a-zA-Z]+$/, // 纯字母
    /^(..)\1+$/, // 重复字符
    /(012|123|234|345|456|567|678|789|890|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i, // 连续字符
    /(qwerty|asdfgh|zxcvbn|qazwsx|123456|password|admin|user|guest)/i, // 常见弱密码
  ];

  for (const weakPattern of weakPatterns) {
    if (weakPattern.test(password)) {
      return {
        isValid: false,
        message: '密码存在常见弱密码模式，请设置更复杂的密码',
        score: Math.min(score, 2),
      };
    }
  }

  return {
    isValid: true,
    message: '密码强度符合要求',
    score: Math.min(score, 5),
  };
}

/**
 * 密码强度简单检查（用于 Element Plus 表单验证）
 * @param password 密码字符串
 * @returns 是否通过验证
 */
export function isStrongPassword(password: string): boolean {
  return validatePasswordStrength(password).isValid;
}
